<template>
  <div>
    <section-title content="Información de Dirección" :has-line="true" />
    <div class="address-information-list mt-3">
      <custom-callout
        v-for="(address, idx) in value"
        :key="idx"
        class="mb-2"
        :show-edition="
          !disabled &&
            editMode &&
            editInfo.data != null &&
            editInfo.index == idx
        "
      >
        <div class="row">
          <div class="col-sm-1 text-center align-middle mt-3">
            <div class="dot text-center color-white">
              <i class="fas fa-map-marker-alt" />
            </div>
          </div>
          <div class="col-sm-2 cell">
            <div class="data">
              <span>Tipo:</span>
              <p>
                {{
                  address.addressTypeName == 'Físico'
                    ? 'Física'
                    : address.addressTypeName
                }}
              </p>
            </div>
          </div>
          <div class="col-sm-6 cell">
            <div class="data">
              <span>
                Dirección
                {{
                  address.addressTypeName == 'Físico'
                    ? 'Física'
                    : address.addressTypeName
                }}:
              </span>
              <p>{{ addressFormat(address) }}</p>
            </div>
          </div>
          <div
            v-if="!disabled && canPerformActions"
            class="col-sm cell actions"
          >
            <drop-down :data-item="idx" :items="getAvailableActions(address)" />
          </div>
        </div>

        <template v-if="editMode && editInfo.index == idx" #edition>
          <address-information-add-edit
            v-model="editInfo.data"
            :address-types="availableAddressTypes"
            :disable-primary="value.length < 2"
            :fiscal-year-id="fiscalYearId"
            @on-cancel="onAddressCanceled"
            @on-saved="onAddressSaved"
          />
        </template>
      </custom-callout>
      <custom-callout
        class="mb-2"
        :show-container="false"
        :show-edition="!disabled && createMode"
      >
        <template #edition>
          <address-information-add-edit
            v-model="editInfo.data"
            :address-types="availableAddressTypes"
            :disable-primary="value.length == 0"
            :fiscal-year-id="fiscalYearId"
            @on-cancel="onAddressCanceled"
            @on-saved="onAddressSaved"
          />
        </template>
      </custom-callout>
      <div v-if="!disabled" class="row mt-3">
        <div class="col">
          <action-button
            v-if="editInfo.data == null && canAddAddress"
            class="float-right"
            i="fas fa-plus"
            text="Añadir"
            variant="success"
            @click="newAddress"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from '@/shared/components/SectionTitle';
import CustomCallout from '@/components/common/CustomCallout';
import AddressInformationAddEdit from './AddressInformationAddEdit';

import { getAddressTypes as _getAddressTypes } from '@/modules/provider/api';

import alert from '@/mixins/alert.mixin';

export default {
  name: 'AddressInformationList',
  components: {
    CustomCallout,
    AddressInformationAddEdit,
    SectionTitle,
  },
  mixins: [alert],
  props: {
    value: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    onSaving: {
      type: Function,
      default: () => {},
    },
    onDeleting: {
      type: Function,
      default: () => {},
    },
    fiscalYearId: {
      type: Number,
      default: () => null,
    },
    addEnabled: {
      type: Boolean,
      default: true,
    },
    editEnabled: {
      type: Boolean,
      default: true,
    },
    deleteEnabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      editInfo: {
        index: 0,
        data: null,
      },
      editMode: false,
      createMode: false,
      existingAddressTypes: [],
      addressTypes: [],
    };
  },
  computed: {
    canAddAddress() {
      return this.addEnabled;
    },
    availableAddressTypes() {
      this.value.forEach((element) => {
        this.addressTypes = this.addressTypes.filter(
          (x) => x.id != element.addressTypeCd
        );
      });

      return this.addressTypes;
    },
    canPerformActions() {
      return this.editEnabled || this.deleteEnabled;
    },
    addressFormat() {
      return (address) =>
        address.addressLine1 +
        (address.addressLine2 != null ? ' ' + address.addressLine2 : '') +
        (address.addressLine3 != null ? ' ' + address.addressLine3 : '') +
        ' ' +
        address.municipalityName +
        ', PR ' +
        address.zipCode;
    },
  },
  watch: {
    value: function(newValue) {
      if (newValue.length == 0 && !this.disabled) {
        this.newAddress();
      }
    },
  },
  async created() {
    await _getAddressTypes().then((response) => {
      this.addressTypes.push.apply(this.addressTypes, response.data);
    });
  },
  methods: {
    getAvailableActions(item) {
      let actions = [
        {
          name: 'edit',
          label: 'Editar',
          icon: 'fas fa-edit color-primary-1',
          click: this.onEdit,
          visible: !this.disabled && this.editEnabled,
        },
        {
          name: 'delete',
          label: 'Eliminar',
          icon: 'fas fa-times-circle text-danger',
          click: this.onDelete,
          visible:
            !this.disabled && this.deleteEnabled && item.addressTypeCd != 1,
        },
      ];

      return actions;
    },
    newAddress() {
      this.createMode = true;
      this.editMode = false;
      this.editInfo = this.getNewAddressModel();
    },
    onEdit(dataItemIndex) {
      this.editMode = true;
      this.createMode = false;

      this.editInfo = {
        index: dataItemIndex,
        data: this.value[dataItemIndex],
      };
    },
    async onDelete(dataItemIndex) {
      await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea eliminar la dirección seleccionada?',
        'Sí',
        'No'
      ).then(async (answer) => {
        if (answer) {
          let isValid = true;

          if (this.onDeleting != undefined)
            isValid = await this.onDeleting(
              this.value[dataItemIndex].addressId
            );

          if (isValid) {
            this.value.splice(dataItemIndex, 1);
          }
        }
      });
    },
    async onAddressSaved(savedAddress) {
      let isValid = true;

      if (this.onSaving != undefined)
        isValid = await this.onSaving(savedAddress);

      if (isValid) {
        if (savedAddress.isNew) {
          savedAddress.isNew = false;
          this.value.push(savedAddress);
        } else {
          var foundIndex = this.value.findIndex(
            (x) => x.addressId == savedAddress.addressId
          );
          this.value[foundIndex] = savedAddress;
        }

        this.clearEditCreate();
      }
    },
    async onAddressCanceled() {
      this.clearEditCreate();
    },
    clearEditCreate() {
      this.createMode = false;
      this.editMode = false;

      this.editInfo = {
        index: 0,
        data: null,
      };
    },
    getNewAddressModel() {
      const newAddressModel = {
        data: {
          addressId: null,
          addressLine1: null,
          addressLine2: null,
          addressLine3: null,
          zipCode: null,
          addressTypeCd: null,
          municipalityId: null,
          gpsLatitude: null,
          gpsLongitude: null,
          addressDescription: null,
          isNew: true,
        },
      };

      return newAddressModel;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/shared/scss/config/fonts.scss';
@import '~@/shared/scss/config/colors.scss';

.address-information-list .cell {
  display: table-cell !important;
  vertical-align: middle;
  margin-left: 2.2rem;
}

.address-information-list .cell span {
  color: map-get($colors, 'gray-6');
  font-size: map-get($fontsSize, 'font-14');
}

.address-information-list .cell p {
  color: map-get($colors, 'black');
  font-size: map-get($fontsSize, 'font-16');
  margin-bottom: 0;
  margin-top: 10px;
}

.address-information-list .cell.actions {
  text-align: right;
  margin-top: 17px;
}
.dot {
  background-color: #ff7058;
  box-shadow: 0px 3px 6px #00000029;
  margin: 2px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border-style: solid;
  border-width: 3px;
  border-color: #fff;
  display: inline-block;
}
.is-big-icon {
  margin-left: 3px;
}
.color-white {
  color: white;
}
</style>
